import DeleteIcon from '../components/DeleteIcon';
import AppIcon from '../components/AppIcon';
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import './OnboardingTrabalhador.css';

const TABS = [
    { id: 'pessoais', label: '👤 Dados' },
    { id: 'identificacao', label: '🪪 Identificação' },
    { id: 'banco', label: '🏦 IBAN' },
    { id: 'assinatura', label: '🖊️ Assinatura' },
    { id: 'documentos', label: '📋 Documentos' }
];

export default function OnboardingTrabalhador() {
    const { token } = useParams();
    const [loading, setLoading] = useState(true);
    const [erroFatal, setErroFatal] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submetido, setSubmetido] = useState(false);
    const [erro, setErro] = useState('');
    const [activeTab, setActiveTab] = useState('pessoais');
    const [temContrato, setTemContrato] = useState(false);
    const [contratoLido, setContratoLido] = useState(false);
    const [documentos, setDocumentos] = useState([]);
    const [aceitando, setAceitando] = useState({});
    const [form, setForm] = useState({
        nome: '',
        residencia: '',
        codigo_postal: '',
        localidade: '',
        telefone: '',
        telefone_fixo: '',
        telefone_alt: '',
        email: '',
        contacto_emergencia: '',
        data_nascimento: '',
        nacionalidade: 'Portuguesa',
        naturalidade_distrito: '',
        naturalidade_concelho: '',
        naturalidade_freguesia: '',
        habilitacoes_literarias: '',
        doc_identificacao_tipo: 'CC',
        doc_identificacao_num: '',
        doc_identificacao_emissao: '',
        doc_identificacao_validade: '',
        nif: '',
        niss: '',
        iban: '',
        assinatura_url: '',
        foto_url: ''
    });

    // Modo de assinatura: 'desenhar' | 'foto'
    const [sigMode, setSigMode] = useState('desenhar');
    const sigCanvasRef = useRef(null);
    const sigDrawing = useRef(false);

    // Carregar dados do token
    useEffect(() => {
        api.get(`/onboarding/${token}`)
            .then(r => {
                const data = r.data;
                const isDateKey = (k) => k.startsWith('data_') || k.endsWith('_validade') || k.endsWith('_emissao');
                setForm(f => ({
                    ...f,
                    ...Object.fromEntries(
                        Object.keys(f).map(k => [k, data[k] != null ? (isDateKey(k) ? String(data[k] || '').slice(0, 10) : data[k]) : f[k]])
                    )
                }));
                setTemContrato(!!data.tem_contrato);
                if (data.ja_completado) {
                    setErroFatal('Este link já foi utilizado. Se precisar de alterar dados, contacte o administrador.');
                }
            })
            .catch(err => {
                setErroFatal(err.response?.data?.error || 'Não foi possível carregar o link.');
            })
            .finally(() => setLoading(false));
    }, [token]);

    const updateField = (k, v) => setForm(f => ({ ...f, [k]: v }));

    // ========= Foto de perfil =========
    const handleFotoChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const fd = new FormData();
        fd.append('foto', file);
        try {
            const r = await api.post(`/onboarding/${token}/foto`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            updateField('foto_url', r.data.foto_url);
        } catch (err) {
            setErro('Erro ao carregar foto: ' + (err.response?.data?.error || err.message));
        }
    };

    // ========= Assinatura: desenhar no canvas =========
    const getCanvasPos = (e) => {
        const canvas = sigCanvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        return {
            x: (clientX - rect.left) * scaleX,
            y: (clientY - rect.top) * scaleY
        };
    };
    const sigStart = (e) => {
        e.preventDefault();
        const canvas = sigCanvasRef.current; if (!canvas) return;
        sigDrawing.current = true;
        const pos = getCanvasPos(e);
        const ctx = canvas.getContext('2d');
        ctx.beginPath();
        ctx.moveTo(pos.x, pos.y);
        ctx.lineWidth = 2.5;
        ctx.lineCap = 'round';
        ctx.strokeStyle = '#111827';
    };
    const sigMove = (e) => {
        if (!sigDrawing.current) return;
        e.preventDefault();
        const canvas = sigCanvasRef.current; if (!canvas) return;
        const pos = getCanvasPos(e);
        const ctx = canvas.getContext('2d');
        ctx.lineTo(pos.x, pos.y);
        ctx.stroke();
    };
    const sigEnd = () => { sigDrawing.current = false; };
    const sigClear = () => {
        const canvas = sigCanvasRef.current; if (!canvas) return;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        updateField('assinatura_url', '');
    };
    const sigConfirm = () => {
        const canvas = sigCanvasRef.current; if (!canvas) return;
        updateField('assinatura_url', canvas.toDataURL('image/png'));
    };

    // ========= Assinatura: upload imagem =========
    const handleSigUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => updateField('assinatura_url', reader.result);
        reader.readAsDataURL(file);
    };

    // ========= Assinatura: foto com remoção de fundo =========
    // Algoritmo: lê a imagem, calcula luminância de cada pixel; pixels claros (>= threshold)
    // ficam transparentes; pixels escuros viram pretos. Funciona bem em fotos de assinaturas
    // em folha branca tiradas com telemóvel.
    const handleSigPhoto = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const img = new Image();
        const reader = new FileReader();
        reader.onload = () => { img.src = reader.result; };
        img.onload = () => {
            // Reduzir tamanho máximo para output mais leve
            const maxW = 1000;
            const scale = img.width > maxW ? maxW / img.width : 1;
            const w = Math.round(img.width * scale);
            const h = Math.round(img.height * scale);
            const canvas = document.createElement('canvas');
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, w, h);
            const data = ctx.getImageData(0, 0, w, h);
            const px = data.data;
            // Threshold adaptativo: usar luminância média + offset
            let sum = 0;
            for (let i = 0; i < px.length; i += 4) {
                sum += 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2];
            }
            const meanLum = sum / (px.length / 4);
            const threshold = Math.max(110, meanLum - 35);
            for (let i = 0; i < px.length; i += 4) {
                const lum = 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2];
                if (lum >= threshold) {
                    px[i + 3] = 0; // fundo claro → transparente
                } else {
                    // tinta → preto sólido
                    px[i] = 17; px[i + 1] = 24; px[i + 2] = 39; px[i + 3] = 255;
                }
            }
            ctx.putImageData(data, 0, 0);
            updateField('assinatura_url', canvas.toDataURL('image/png'));
        };
        reader.readAsDataURL(file);
    };

    // ========= Carregar lista de documentos =========
    const carregarDocumentos = async () => {
        try {
            const r = await api.get(`/onboarding/${token}/documentos`);
            setDocumentos(Array.isArray(r.data) ? r.data : []);
        } catch { setDocumentos([]); }
    };

    const aceitarDocumento = async (doc) => {
        if (!form.assinatura_url) {
            setErro('Vai ao separador "Assinatura" e regista a assinatura primeiro.');
            return;
        }
        if (doc.is_contrato && !contratoLido) {
            setErro('Abre e lê o contrato antes de o aceitar.');
            return;
        }
        // Garantir que a assinatura está guardada no backend
        await api.put(`/onboarding/${token}`, { assinatura_url: form.assinatura_url }).catch(() => {});

        setAceitando(prev => ({ ...prev, [doc.nome]: true }));
        try {
            await api.post(`/onboarding/${token}/aceitar-documento`, { documento: doc.nome });
            setDocumentos(prev => prev.map(d =>
                d.nome === doc.nome ? { ...d, aceite: true, data_aceitacao: new Date().toISOString() } : d
            ));
            setErro('');
        } catch (err) {
            setErro(err.response?.data?.error || 'Erro ao aceitar documento');
        } finally {
            setAceitando(prev => ({ ...prev, [doc.nome]: false }));
        }
    };

    // ========= Abrir contrato individual =========
    const abrirContrato = async () => {
        try {
            const res = await fetch(`${api.defaults.baseURL}/onboarding/${token}/contrato`);
            if (!res.ok) throw new Error();
            const blob = await res.blob();
            window.open(URL.createObjectURL(blob), '_blank');
            setContratoLido(true);
        } catch {
            setErro('Não foi possível abrir o contrato.');
        }
    };

    // ========= Guardar parcial =========
    const guardarParcial = async () => {
        try {
            await api.put(`/onboarding/${token}`, form);
            return true;
        } catch (err) {
            setErro(err.response?.data?.error || 'Erro a guardar dados');
            return false;
        }
    };

    // ========= Submeter (marcar onboarding como concluído) =========
    const handleSubmit = async () => {
        setErro('');
        if (!form.nome) { setErro('Nome é obrigatório.'); setActiveTab('pessoais'); return; }
        if (!form.assinatura_url) { setErro('A assinatura é obrigatória.'); setActiveTab('assinatura'); return; }

        const contratoDoc = documentos.find(d => d.is_contrato);
        if (contratoDoc && !contratoDoc.aceite) { setErro('Tens de aceitar o Contrato de Trabalho antes de submeter.'); return; }

        setSubmitting(true);
        try {
            await api.put(`/onboarding/${token}`, form);
            await api.post(`/onboarding/${token}/finalizar`, {
                assinatura_url: form.assinatura_url
            });
            setSubmetido(true);
        } catch (err) {
            setErro(err.response?.data?.error || 'Erro ao submeter');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <div className="onb-shell"><div className="onb-loading">A carregar...</div></div>;
    if (erroFatal) {
        return (
            <div className="onb-shell">
                <div className="onb-card">
                    <div className="onb-error-screen">⚠️ {erroFatal}</div>
                </div>
            </div>
        );
    }
    if (submetido) {
        return (
            <div className="onb-shell">
                <div className="onb-card">
                    <div className="onb-header">
                        <h1>Obrigado!</h1>
                        <p>Os seus dados foram submetidos com sucesso.</p>
                    </div>
                    <div className="onb-body">
                        <div className="onb-msg-success">
                            ✅ Recebemos a sua ficha e o contrato assinado.<br/>
                            Guarde uma cópia do contrato assinado para os seus registos.
                        </div>
                        <div className="onb-mobile-manuals-note" role="note">
                            <span className="onb-mobile-manuals-note-icon"><AppIcon name="document" size={20} /></span>
                            <div>
                                <strong>Manuais disponíveis na aplicação mobile</strong>
                                <p>Quando receber as credenciais da aplicação, encontra no menu principal as áreas <strong>Manuais</strong> e <strong>Documentos</strong>. Em Documentos pode consultar os manuais e os restantes documentos publicados; em Manuais encontra os guias rápidos da aplicação.</p>
                            </div>
                        </div>
                        <div style={{ marginTop: 16, textAlign: 'center' }}>
                            <button className="onb-btn onb-btn-primary" onClick={async () => {
                                try {
                                    const res = await fetch(`${api.defaults.baseURL}/onboarding/${token}/contrato/signed`);
                                    if (!res.ok) throw new Error();
                                    const blob = await res.blob();
                                    const a = document.createElement('a');
                                    a.href = URL.createObjectURL(blob);
                                    a.download = 'contrato_assinado.pdf';
                                    a.click();
                                } catch { alert('Erro ao descarregar contrato assinado.'); }
                            }}>
                                📄 Descarregar Contrato Assinado
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="onb-shell">
            <div className="onb-card">
                <div className="onb-header">
                    <h1>Preenchimento de Ficha de Trabalhador</h1>
                    <p>Olá {form.nome || ''} — preencha os campos em falta e assine para concluir.</p>
                </div>

                <div className="onb-body">
                    <div className="onb-tabs">
                        {TABS.map(t => (
                            <div key={t.id}
                                 className={`onb-tab ${activeTab === t.id ? 'active' : ''}`}
                                 onClick={() => {
                                     setActiveTab(t.id);
                                     if (t.id === 'documentos') carregarDocumentos();
                                 }}>
                                {t.label}
                            </div>
                        ))}
                    </div>

                    {erro && <div className="onb-msg-error">{erro}</div>}

                    {activeTab === 'pessoais' && (
                        <div className="onb-section">
                            <div className="onb-photo-row">
                                <div className="onb-photo-preview">
                                    {form.foto_url
                                        ? <img src={`${api.defaults.baseURL.split('/api')[0]}${form.foto_url}`} alt="" />
                                        : <span style={{ fontSize: 36, color: '#9ca3af' }}>👤</span>}
                                </div>
                                <div className="onb-photo-actions">
                                    <label className="onb-btn onb-btn-secondary" style={{ marginBottom: 0 }}>
                                        📁 Carregar foto
                                        <input type="file" accept="image/*" onChange={handleFotoChange} style={{ display: 'none' }} />
                                    </label>
                                    <label className="onb-btn onb-btn-secondary" style={{ marginBottom: 0 }}>
                                        📸 Tirar foto
                                        <input type="file" accept="image/*" capture="user" onChange={handleFotoChange} style={{ display: 'none' }} />
                                    </label>
                                </div>
                            </div>

                            <div className="onb-grid" style={{ marginTop: 16 }}>
                                <div className="onb-field full">
                                    <label>Nome Completo *</label>
                                    <input value={form.nome} onChange={e => updateField('nome', e.target.value)} />
                                </div>
                                <div className="onb-field full">
                                    <label>Residência</label>
                                    <input value={form.residencia} onChange={e => updateField('residencia', e.target.value)} />
                                </div>
                                <div className="onb-field">
                                    <label>Código Postal</label>
                                    <input value={form.codigo_postal} onChange={e => updateField('codigo_postal', e.target.value)} placeholder="1234-123" />
                                </div>
                                <div className="onb-field">
                                    <label>Localidade</label>
                                    <input value={form.localidade} onChange={e => updateField('localidade', e.target.value)} />
                                </div>
                                <div className="onb-field">
                                    <label>Telemóvel</label>
                                    <input type="tel" value={form.telefone} onChange={e => updateField('telefone', e.target.value)} />
                                </div>
                                <div className="onb-field">
                                    <label>Telefone 1</label>
                                    <input type="tel" value={form.telefone_fixo} onChange={e => updateField('telefone_fixo', e.target.value)} />
                                </div>
                                <div className="onb-field">
                                    <label>Telefone 2</label>
                                    <input type="tel" value={form.telefone_alt} onChange={e => updateField('telefone_alt', e.target.value)} />
                                </div>
                                <div className="onb-field">
                                    <label>Email</label>
                                    <input type="email" value={form.email} onChange={e => updateField('email', e.target.value)} />
                                </div>
                                <div className="onb-field">
                                    <label>Data de Nascimento</label>
                                    <input type="date" value={form.data_nascimento || ''} onChange={e => updateField('data_nascimento', e.target.value)} />
                                </div>
                                <div className="onb-field">
                                    <label>Nacionalidade</label>
                                    <input value={form.nacionalidade} onChange={e => updateField('nacionalidade', e.target.value)} />
                                </div>
                                <div className="onb-field full">
                                    <label>Contacto de Emergência</label>
                                    <input value={form.contacto_emergencia} onChange={e => updateField('contacto_emergencia', e.target.value)} placeholder="Nome e telefone" />
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'identificacao' && (
                        <div className="onb-section">
                            <div className="onb-grid">
                                <div className="onb-field">
                                    <label>Tipo de Documento</label>
                                    <select value={form.doc_identificacao_tipo} onChange={e => updateField('doc_identificacao_tipo', e.target.value)}>
                                        <option value="CC">Cartão de Cidadão</option>
                                        <option value="Passaporte">Passaporte</option>
                                        <option value="AutorizacaoResidencia">Autorização de Residência</option>
                                    </select>
                                </div>
                                <div className="onb-field">
                                    <label>Nº do Documento</label>
                                    <input value={form.doc_identificacao_num} onChange={e => updateField('doc_identificacao_num', e.target.value)} />
                                </div>
                                <div className="onb-field">
                                    <label>Data de Emissão</label>
                                    <input type="date" value={form.doc_identificacao_emissao || ''} onChange={e => updateField('doc_identificacao_emissao', e.target.value)} />
                                </div>
                                <div className="onb-field">
                                    <label>Validade do Documento</label>
                                    <input type="date" value={form.doc_identificacao_validade || ''} onChange={e => updateField('doc_identificacao_validade', e.target.value)} />
                                </div>
                                <div className="onb-field">
                                    <label>NIF</label>
                                    <input value={form.nif} onChange={e => updateField('nif', e.target.value)} />
                                </div>
                                <div className="onb-field">
                                    <label>NISS (Segurança Social)</label>
                                    <input value={form.niss} onChange={e => updateField('niss', e.target.value)} />
                                </div>
                                <div className="onb-field">
                                    <label>Habilitações Literárias</label>
                                    <select value={form.habilitacoes_literarias || ''} onChange={e => updateField('habilitacoes_literarias', e.target.value)}>
                                        <option value="">Selecione...</option>
                                        <option value="Sem escolaridade">Sem escolaridade</option>
                                        <option value="1º Ciclo (4ª classe)">1º Ciclo (4ª classe)</option>
                                        <option value="2º Ciclo (6º ano)">2º Ciclo (6º ano)</option>
                                        <option value="3º Ciclo (9º ano)">3º Ciclo (9º ano)</option>
                                        <option value="Ensino Secundário">Ensino Secundário</option>
                                        <option value="Curso Profissional">Curso Profissional</option>
                                        <option value="Bacharelato">Bacharelato</option>
                                        <option value="Licenciatura">Licenciatura</option>
                                        <option value="Mestrado">Mestrado</option>
                                        <option value="Doutoramento">Doutoramento</option>
                                    </select>
                                </div>

                                <div className="onb-field full" style={{ marginTop: 8 }}>
                                    <label style={{ color: '#1e3a8a', fontWeight: 700 }}>📍 Naturalidade</label>
                                </div>
                                <div className="onb-field">
                                    <label>Distrito</label>
                                    <input value={form.naturalidade_distrito} onChange={e => updateField('naturalidade_distrito', e.target.value)} />
                                </div>
                                <div className="onb-field">
                                    <label>Concelho</label>
                                    <input value={form.naturalidade_concelho} onChange={e => updateField('naturalidade_concelho', e.target.value)} />
                                </div>
                                <div className="onb-field full">
                                    <label>Freguesia</label>
                                    <input value={form.naturalidade_freguesia} onChange={e => updateField('naturalidade_freguesia', e.target.value)} />
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'banco' && (
                        <div className="onb-section">
                            <div className="onb-grid">
                                <div className="onb-field full">
                                    <label>IBAN</label>
                                    <input value={form.iban} onChange={e => updateField('iban', e.target.value.toUpperCase())} placeholder="PT50 0000 0000 0000 0000 0000 0" />
                                </div>
                            </div>
                            <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: 8 }}>
                                O IBAN será utilizado para o pagamento do salário.
                            </p>
                        </div>
                    )}

                    {activeTab === 'assinatura' && (
                        <div className="onb-section">
                            <div className="onb-tabs" style={{ marginBottom: 14 }}>
                                <div className={`onb-tab ${sigMode === 'desenhar' ? 'active' : ''}`} onClick={() => setSigMode('desenhar')}>✏️ Desenhar</div>
                                <div className={`onb-tab ${sigMode === 'foto' ? 'active' : ''}`} onClick={() => setSigMode('foto')}>📸 Foto</div>
                                <div className={`onb-tab ${sigMode === 'upload' ? 'active' : ''}`} onClick={() => setSigMode('upload')}>📁 Upload</div>
                            </div>

                            {sigMode === 'desenhar' && (
                                <>
                                    <div className="onb-sig-canvas-wrap">
                                        <canvas
                                            ref={sigCanvasRef}
                                            className="onb-sig-canvas"
                                            width={800}
                                            height={300}
                                            onMouseDown={sigStart} onMouseMove={sigMove}
                                            onMouseUp={sigEnd} onMouseLeave={sigEnd}
                                            onTouchStart={sigStart} onTouchMove={sigMove} onTouchEnd={sigEnd}
                                        />
                                    </div>
                                    <div className="onb-sig-actions">
                                        <button className="onb-btn onb-btn-secondary" onClick={sigClear}><DeleteIcon /> Limpar</button>
                                        <button className="onb-btn onb-btn-primary" onClick={sigConfirm}>✅ Confirmar Assinatura</button>
                                    </div>
                                </>
                            )}

                            {sigMode === 'foto' && (
                                <>
                                    <p style={{ fontSize: '0.88rem', color: '#4b5563', margin: '0 0 10px' }}>
                                        Assine numa folha branca e tire foto. O fundo será automaticamente removido.
                                    </p>
                                    <label className="onb-btn onb-btn-primary">
                                        📸 Tirar foto da assinatura
                                        <input type="file" accept="image/*" capture="environment" onChange={handleSigPhoto} style={{ display: 'none' }} />
                                    </label>
                                </>
                            )}

                            {sigMode === 'upload' && (
                                <label className="onb-btn onb-btn-primary">
                                    📁 Carregar imagem (PNG/JPG)
                                    <input type="file" accept="image/*" onChange={handleSigUpload} style={{ display: 'none' }} />
                                </label>
                            )}

                            {form.assinatura_url && (
                                <div className="onb-sig-preview" style={{ marginTop: 16 }}>
                                    <div style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: 8 }}>Pré-visualização:</div>
                                    <img src={form.assinatura_url} alt="Assinatura" />
                                    <div style={{ marginTop: 8 }}>
                                        <button className="onb-btn onb-btn-secondary" onClick={() => updateField('assinatura_url', '')}><DeleteIcon /> Remover</button>
                                    </div>
                                </div>
                            )}

                            {form.assinatura_url && (
                                <div style={{ marginTop: 14, padding: '10px 14px', background: '#f0fff4', border: '1px solid #86efac', borderRadius: 8, fontSize: '0.85rem', color: '#15803d' }}>
                                    ✅ Assinatura registada. Passa ao separador <strong>Documentos</strong> para aceitar os documentos e concluir.
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'documentos' && (
                        <div className="onb-section">
                            {!form.assinatura_url && (
                                <div className="onb-msg-error" style={{ marginBottom: 14 }}>
                                    ⚠️ Vai ao separador <strong>Assinatura</strong> e regista a tua assinatura antes de aceitar documentos.
                                </div>
                            )}
                            <p style={{ fontSize: '0.87rem', color: '#4b5563', marginBottom: 14 }}>
                                Lê cada documento e aceita-o com a tua assinatura. O contrato deve ser aceite para concluir.
                            </p>
                        <div className="onb-mobile-manuals-note" role="note">
                            <span className="onb-mobile-manuals-note-icon"><AppIcon name="document" size={20} /></span>
                            <div>
                                <strong>Manuais disponíveis na aplicação mobile</strong>
                                <p>Quando receber as credenciais da aplicação, encontra no menu principal as áreas <strong>Manuais</strong> e <strong>Documentos</strong>. Em Documentos pode consultar os manuais e os restantes documentos publicados; em Manuais encontra os guias rápidos da aplicação.</p>
                            </div>
                        </div>

                            {documentos.length === 0 && (
                                <div style={{ textAlign: 'center', color: '#9ca3af', padding: '20px 0' }}>
                                    A carregar documentos...
                                </div>
                            )}

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {documentos.map(doc => (
                                    <div key={doc.nome} style={{
                                        background: doc.aceite ? '#f0fff4' : '#fff',
                                        border: `1px solid ${doc.aceite ? '#86efac' : (doc.is_contrato ? '#fcd34d' : '#e5e7eb')}`,
                                        borderRadius: 10,
                                        padding: '12px 14px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 10,
                                        flexWrap: 'wrap'
                                    }}>
                                        <div style={{ flex: 1, minWidth: 180 }}>
                                            <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#1f2937' }}>
                                                {doc.is_contrato ? '📑 ' : '📄 '}{doc.nome}
                                                {doc.is_contrato && <span style={{ fontSize: '0.72rem', marginLeft: 6, background: '#fef3c7', color: '#92400e', padding: '1px 6px', borderRadius: 4 }}>Individual</span>}
                                            </div>
                                            <div style={{ fontSize: '0.78rem', color: doc.aceite ? '#16a34a' : '#6b7280', marginTop: 3 }}>
                                                {doc.aceite
                                                    ? `✅ Aceite em ${new Date(doc.data_aceitacao).toLocaleDateString('pt-PT')}`
                                                    : '❌ Pendente'}
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                            {doc.tem_pdf && !doc.aceite && (
                                                <button
                                                    className="onb-btn onb-btn-secondary"
                                                    style={{ padding: '6px 12px', fontSize: '0.82rem' }}
                                                    onClick={() => {
                                                        if (doc.is_contrato) {
                                                            abrirContrato();
                                                        } else {
                                                            fetch(`${api.defaults.baseURL}/onboarding/${token}/documentos-internos/${doc.id}/pdf`)
                                                                .then(r => r.blob())
                                                                .then(b => window.open(URL.createObjectURL(b), '_blank'))
                                                                .catch(() => alert('Erro ao abrir documento'));
                                                        }
                                                    }}>
                                                    📖 Ler
                                                </button>
                                            )}
                                            {!doc.aceite && (
                                                <button
                                                    className="onb-btn onb-btn-primary"
                                                    style={{ padding: '6px 12px', fontSize: '0.82rem' }}
                                                    disabled={aceitando[doc.nome] || !form.assinatura_url || (doc.is_contrato && !contratoLido)}
                                                    onClick={() => aceitarDocumento(doc)}>
                                                    {aceitando[doc.nome] ? 'A aceitar...' : '✍️ Aceitar'}
                                                </button>
                                            )}
                                            {doc.aceite && doc.is_contrato && (
                                                <button
                                                    className="onb-btn onb-btn-secondary"
                                                    style={{ padding: '6px 12px', fontSize: '0.82rem' }}
                                                    onClick={async () => {
                                                        try {
                                                            const res = await fetch(`${api.defaults.baseURL}/onboarding/${token}/contrato/signed`);
                                                            if (!res.ok) throw new Error();
                                                            const a = document.createElement('a');
                                                            a.href = URL.createObjectURL(await res.blob());
                                                            a.download = 'contrato_assinado.pdf';
                                                            a.click();
                                                        } catch { alert('Erro ao descarregar.'); }
                                                    }}>
                                                    📄 PDF Assinado
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {documentos.length > 0 && (
                                <div style={{ marginTop: 20, borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
                                    <button
                                        className="onb-btn onb-btn-success"
                                        style={{ width: '100%', padding: '12px', fontSize: '1rem' }}
                                        onClick={handleSubmit}
                                        disabled={submitting || !documentos.find(d => d.is_contrato)?.aceite}>
                                        {submitting ? 'A submeter...' : '✅ Concluir Preenchimento'}
                                    </button>
                                    {!documentos.find(d => d.is_contrato)?.aceite && (
                                        <p style={{ fontSize: '0.8rem', color: '#9ca3af', textAlign: 'center', marginTop: 8 }}>
                                            Aceita o Contrato de Trabalho para concluir.
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="onb-footer">
                    <button className="onb-btn onb-btn-secondary" onClick={guardarParcial} disabled={submitting}>
                        💾 Guardar e continuar depois
                    </button>
                    {activeTab !== 'documentos' && (
                        <button className="onb-btn onb-btn-primary"
                            style={{ padding: '10px 18px' }}
                            onClick={() => {
                                const idx = TABS.findIndex(t => t.id === activeTab);
                                const next = TABS[idx + 1];
                                if (next) {
                                    setActiveTab(next.id);
                                    if (next.id === 'documentos') carregarDocumentos();
                                }
                            }}>
                            Seguinte →
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
